import React, { Dispatch, SetStateAction } from 'react'
import {
  Badge,
  Button,
  CheckboxGroup,
  Collapse,
  Panel,
  theme
} from '@lmc/ui-kit'
import styled from 'styled-components'
import { Carer, Responsibility, UserRole } from '@lmc/ts-common'
import { getCarerRole } from '../utils'
import { Home } from '@lmc/ts-common'
import useHasSafeguardingEnabled from 'hooks/useHasSafeguardingEnabled'
import { NAMESPACE } from 'locale'
import { useTranslation } from 'react-i18next'
import { teamTranslations } from 'locale/team'

type CheckboxValueType = string | number | boolean

export type TeamSideFiltersProps = {
  carers?: Carer[]
  onClick?: () => void
  onStatuses: Dispatch<SetStateAction<string[]>>
  onRoles: Dispatch<SetStateAction<string[]>>
  statuses: string[]
  roles: string[]
  features: Home
  clearButtonTitle: string
  statusTitle?: string
  roleTitle: string
  onDefaultOpened?: boolean
  viewProTitle?: string
  onSubscribe?: () => void
  testId?: string
}

const StyledCollapse = styled(Collapse)`
  background-color: ${(props) => props.theme.color.neutral.color10} !important;
  border-radius: 8px;
`

const StyledExtraWrapper = styled.div`
  display: flex;
`

const StyledBadge = styled(Badge)`
  width: 24px;
  height: 24px;
  border-radius: 12px;
  background-color: red;
`

const StyledWrapper = styled.div`
  max-width: 345px;
  width: 100%;
  margin-top: 24px;
  padding-left: 24px;
  ${({ theme }) => theme.mediaQueries.mobileDown} {
    margin-bottom: 24px;
    max-width: unset;
    padding-right: 24px;
  }
`

const StyledCheckboxGroup = styled(CheckboxGroup)`
  display: flex !important;
  flex-direction: column !important;

  .ant-checkbox-wrapper {
    margin-left: 0px;
  }
`

const TeamSideFilters: React.FC<TeamSideFiltersProps> = ({
  carers,
  onStatuses,
  onRoles,
  statuses,
  roles,
  clearButtonTitle,
  statusTitle,
  roleTitle,
  onDefaultOpened = true,
  testId
}) => {
  const { t } = useTranslation(NAMESPACE.TEAM)

  const hasSafeguardingHook = useHasSafeguardingEnabled()

  const onChangeStatus = (checkedValues: CheckboxValueType[]) =>
    onStatuses(checkedValues as string[])

  const onChangeRole = (checkedValues: CheckboxValueType[]) =>
    onRoles(checkedValues as string[])

  const getExtra = (
    values: string[],
    onClear: Dispatch<SetStateAction<string[]>>
  ) => {
    const hasValues = !!values?.length

    return (
      <StyledExtraWrapper>
        {hasValues && (
          <Button
            variant="text"
            onClick={(e) => {
              e.stopPropagation()
              onClear([])
            }}
          >
            {clearButtonTitle}
          </Button>
        )}
        <StyledBadge
          size="small"
          text={values?.length.toString()}
          color={
            hasValues ? theme.color.status.primary : theme.color.neutral.color8
          }
          textColor={
            !hasValues
              ? theme.color.neutral.color1
              : theme.color.neutral.color11
          }
          style={{
            height: 24,
            width: 24,
            padding: '4px 0px 0px 9.5px',
            marginLeft: 5,
            paddingRight: 5
          }}
        />
      </StyledExtraWrapper>
    )
  }

  const getStatusCount = (isActive: boolean) =>
    carers?.filter((carer) => (isActive ? carer.active : !carer.active))?.length || 0

  const getRoleCount = (role: string) =>
    carers?.filter((carer) => carer.role === role)?.length || 0

  const getSafeguardingCount = () =>
    carers?.filter((carer) =>
      carer.permissions?.includes(Responsibility.SafeguardingOfficer)
    )?.length || 0

  const roleOptions = [
    {
      value: 'carer',
      label: `${getCarerRole(UserRole.carer)} (${getRoleCount('carer')})`
    },
    {
      value: 'carehome-admin',
      label: `${getCarerRole(UserRole.careHomeAdmin)} (${getRoleCount('carehome-admin')})`
    },
    {
      value: 'senior-carer',
      label: `${getCarerRole(UserRole.seniorCarer)} (${getRoleCount('senior-carer')})`
    },
    ...(hasSafeguardingHook
      ? [
          {
            value: 'safeguarding-officer',
            label: `${t(teamTranslations.SAFEGUARDING_OFFICER)} (${getSafeguardingCount()})`
          }
        ]
      : [])
  ]

  const statusOptions = [
    { value: 'active', label: `Active (${getStatusCount(true)})` },
    { value: 'inactive', label: `Inactive (${getStatusCount(false)})` }
  ]

  return (
    <StyledWrapper data-testid={testId}>
      <StyledCollapse
        defaultActiveKey={onDefaultOpened ? ['1'] : undefined}
        expandIconPosition="start"
        data-testid={testId ? `${testId}-collapse` : undefined}
      >
        <Panel
          header={statusTitle}
          key="1"
          extra={getExtra(statuses, onStatuses)}
          data-testid={testId ? `${testId}-status-panel` : undefined}
        >
          <StyledCheckboxGroup
            onChange={onChangeStatus}
            options={statusOptions}
            value={statuses}
            data-testid={testId ? `${testId}-status-checkboxes` : undefined}
          />
        </Panel>
        <Panel
          header={roleTitle}
          key="2"
          extra={getExtra(roles, onRoles)}
          data-testid={testId ? `${testId}-role-panel` : undefined}
        >
          <StyledCheckboxGroup
            onChange={onChangeRole}
            value={roles}
            options={roleOptions}
            data-testid={testId ? `${testId}-role-checkboxes` : undefined}
          />
        </Panel>
      </StyledCollapse>
    </StyledWrapper>
  )
}

export default TeamSideFilters
